<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <section class="flex flex-col gap-5 border-x border-b rounded-b mb-7 w-full p-4 z-[1] bg-white">
    <section class="relative">
      <h2>
        {{ name }}
      </h2>
      <AppButton
        v-show="!form.wurdeGeladen"
        type="primary"
        text
        circle
        :disabled="form.wurdeGeladen"
        title="entfernen"
        class="absolute top-0 right-0"
        @click="$emit('delete', index)"
      >
        <FaIcon name="xmark-large" variant="regular" />
      </AppButton>
    </section>

    <section class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mb-0">
      <h5 class="mb-2">
        Mein Kind ist
      </h5>
      <FieldError
        v-for="error in formErrors.anrede"
        :key="error"
      >
        {{ error }}
      </FieldError>

      <OptionGroup
        :id="`formError.kinder.${[index]}.anrede`"
        has-icons
        :options="[
          {value: 2, label: 'weiblich', icon: 'icomoon/female',
           labelClasses: 'flex flex-col', iconClass: 'gender'},
          {value: 1, label: 'männlich', icon: 'icomoon/male',
           labelClasses: 'flex flex-col', iconClass: 'gender'},
        ]"
        :value="form.anrede"
        option-width-classes="w-1/3 md:w-1/2"
        @change="set('anrede', $event)"
      />

      <div class="flex flex-col gap-5">
        <ElFormItem
          :id="`formError.kinder.${[index]}.vorname`"
          label="Vorname"
          for="Vorname"
          :error="formErrors.vorname?.[0]"
          label-position="top"
        >
          <ElInput
            id="Vorname"
            :model-value="form.vorname"
            @update:model-value="set('vorname', $event)"
          />
        </ElFormItem>
        <ElFormItem
          :id="`formError.kinder.${[index]}.nachname`"
          label="Nachname"
          for="Nachname"
          :error="formErrors.nachname?.[0]"
          label-position="top"
        >
          <ElInput
            id="Nachname"
            :model-value="form.nachname"
            @update:model-value="set('nachname', $event)"
          />
        </ElFormItem>
        <ElFormItem
          :id="`formError.kinder.${[index]}.geburtsdatum`"
          label="Geburtsdatum"
          for="Geburtsdatum"
          :error="formErrors.geburtsdatum?.[0]"
          label-position="top"
        >
          <DateInput
            id="Geburtsdatum"
            :model-value="form.geburtsdatum"
            @update:model-value="set('geburtsdatum', $event)"
          />
        </ElFormItem>
      </div>
    </section>

    <section
      v-if="isSixteen"
      class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mt-7"
    >
      <h5 class="-mb-3">
        Berufsstatus und Einkommen meines Kindes
      </h5>
      <ElFormItem
        label="Berufsstatus"
        for="Berufsstatus"
        :error="formErrors.taetigkeitsstatus?.[0]"
        label-position="top"
      >
        <ElSelect
          id="Berufsstatus"
          :model-value="form.taetigkeitsstatus"
          placeholder=""
          clearable
          @update:model-value="set('taetigkeitsstatus', $event)"
        >
          <ElOption
            v-for="{ label, value } in taetigkeitsstatus"
            :key="value"
            :label="label"
            :value="value"
          />
        </ElSelect>
      </ElFormItem>
      <ElFormItem
        label="Bruttoeinkommen"
        :error="formErrors.brutto?.[0]"
        label-position="top"
      >
        <MoneyInput
          :model-value="form.brutto"
          as-string
          @update:model-value="set('brutto', handleMoneyValue($event))"
        />
      </ElFormItem>

      <ElRadioGroup
        :model-value="form.isBruttoJahresGehalt ?? undefined"
        class="w-full bg-white"
        :class="isMobile ? 'el-radio-group--vertical' : 'el-radio-group--fill'"
        @update:model-value="form.isBruttoJahresGehalt = $event === true"
      >
        <ElRadioButton :value="true">
          im Jahr
        </ElRadioButton>

        <ElRadioButton :value="false">
          im Monat
        </ElRadioButton>
      </ElRadioGroup>
      <ElFormItem
        label="Nettoeinkommen"
        :error="formErrors.netto?.[0]"
        label-position="top"
      >
        <MoneyInput
          :model-value="form.netto"
          as-string
          @update:model-value="set('netto', handleMoneyValue($event))"
        />
      </ElFormItem>

      <ElRadioGroup
        :model-value="form.isNettoJahresGehalt ?? undefined"
        class="w-full bg-white"
        :class="isMobile ? 'el-radio-group--vertical' : 'el-radio-group--fill'"
        @update:model-value="form.isNettoJahresGehalt = $event === true"
      >
        <ElRadioButton :value="true">
          im Jahr
        </ElRadioButton>

        <ElRadioButton :value="false">
          im Monat
        </ElRadioButton>
      </ElRadioGroup>
    </section>

    <section class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mt-7">
      <h5 class="mb-5">
        Der Krankenversicherungsstatus meines Kindes lautet
      </h5>
      <FieldError
        v-for="error in formErrors.krankenversicherungsstatus"
        :key="error"
      >
        {{ error }}
      </FieldError>
      <ElRadioGroup
        :model-value="form.krankenversicherungsstatus ?? undefined"
        class="w-full bg-white el-radio-group--vertical"
        @update:model-value="form.krankenversicherungsstatus = Number($event)"
      >
        <ElRadioButton
          v-for="{label, value} in krankenversicherungsstatus"
          :key="value"
          :value="value"
        >
          {{ label }}
        </ElRadioButton>
      </ElRadioGroup>
    </section>

    <section class="flex flex-col gap-5 w-full lg:w-2/3 mx-auto mt-7">
      <h5 class="mb-5">
        Mein Kind leidet an einer schweren Krankheit
        <AppButton
          class="ml-2 hint-icon"
          type="primary"
          plain
          circle
          size="small"
          title="Weitere Infos anzeigen"
          @click="showModal = true"
        >
          <FaIcon name="info" />
        </AppButton>
      </h5>
      <FieldError
        v-for="error in formErrors.hatSchwereKrankheit"
        :key="error"
      >
        {{ error }}
      </FieldError>

      <OptionGroup
        :id="`formError.kinder.${[index]}.hatSchwereKrankheit`"
        has-icons
        :options="[
          {label: 'Ja', labelClasses: `${form.hatSchwereKrankheit === 1 ? 'text-green-700' : 'flex flex-col'}`, value: 1, icon: 'icomoon/check', iconClass: `check ${form.hatSchwereKrankheit === 1 ? 'fill-green-700' : ''}`},
          {label: 'Nein', labelClasses: `${form.hatSchwereKrankheit === 0 ? 'text-red-700' : 'flex flex-col'}`, value: 0, icon: 'icomoon/close', iconClass: `close ${form.hatSchwereKrankheit === 0 ? 'fill-red-700' : ''}`}
        ]"
        :value="form.hatSchwereKrankheit"
        option-width-classes="w-1/3"
        @change="set('hatSchwereKrankheit', $event)"
      />

      <div v-if="form.hatSchwereKrankheit">
        <ElFormItem
          label="Diagnose"
          for="Diagnose"
          :error="formErrors.diagnose?.[0]"
          label-position="top"
        >
          <ElInput
            id="Diagnose"
            type="textarea"
            :model-value="form.diagnose ?? ''"
            :name="`kind_${index}_diagnose`"
            :placeholder="`Bitte ${duzen('gebe hier deine', 'geben Sie hier Ihre')} Diagnose ein.`"
            :rows="8"
            @update:model-value="form.diagnose = $event === '' ? null : $event"
          />
        </ElFormItem>
      </div>
    </section>

    <SchwereKrankheitenModal v-model:show="showModal" />
  </section>
</template>

<script setup lang="ts">
import FieldError from '@/application/components/ui/FieldError.vue';
import AppButton from '@/application/components/ui/AppButton.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ElFormItem, ElInput, ElOption, ElRadioButton, ElRadioGroup, ElSelect } from 'element-plus';
import { differenceInYears, parse } from 'date-fns';
import { sortBy } from 'lodash-es';
import { computed, ref } from 'vue';

import DateInput from '@/application/components/form-elements/DateInput/DateInput.vue';
import MoneyInput from '@/application/components/form-elements/MoneyInput/MoneyInput.vue';
import { useDetectMobile } from '@/application/composables/detectMobile';
import OptionGroup from '@/checkup/components/OptionGroup.vue';
import SchwereKrankheitenModal from '@/checkup/components/steps/SchwereKrankheitenModal.vue';
import { toLabelAndValue } from '@/checkup/utils/demvWerte';
import { demvWerte } from '@/checkup/state';
import type { Kind } from '@/checkup/types/types';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

interface Props {
  index: number;
  form: Kind;
  formErrors?: Record<string, string[]>;
}

const props = withDefaults(defineProps<Props>(), {
  formErrors: () => ({}),
})
const emit = defineEmits(['delete', 'clear'])

const { isMobile } = useDetectMobile();
const { duzen } = useBaseDataStore();

const showModal = ref(false);

const name = computed(() => (props.form.vorname
  ? `${props.form.vorname} ${props.form.nachname || ''}`.trim()
  : `Kind ${Number(props.index) + 1}`)
);

const isSixteen = computed(() => {
  if (!props.form.geburtsdatum) return false;

  const now = new Date();
  const input = parse(props.form.geburtsdatum, 'dd.MM.yyyy', now);
  const age = differenceInYears(now, input);

  return !Number.isNaN(age) && age >= 16;
})

const taetigkeitsstatus = sortBy(toLabelAndValue(demvWerte.taetigkeitstatus), 'label');
const krankenversicherungsstatus = toLabelAndValue(demvWerte.krankenversicherungsstatus);

const set = (field: string, value: number | string | boolean | null) => {
  // eslint-disable-next-line vue/no-mutating-props
  props.form[field] = value;
  emit('clear', `${field}`);
};

// TODO: remove when backend accepts value as float
function handleMoneyValue(value: number | string | null): number | string | null {
  if (value === null || value === '0' || value === 0) return null;
  if (typeof value === 'number') return value.toString();
  return value;
}
</script>
